import type { IChat } from '@/lib/types/chat';

/**
 * What an address that opens a "new chat" allows: an agent's frame, a
 * project's, or the guided presets it hosts. A plain ``/chat/new`` has none.
 */
export interface DraftScope {
  agentId?: string | null;
  projectId?: string | null;
  /** The guided presets whose draft this address shows (KAN-1130). */
  guidedPresets?: readonly string[] | null;
}

/** Whether a draft belongs on the new-chat address of ``scope``. */
export const draftMatchesScope = (chat: IChat, scope: DraftScope): boolean =>
  (chat.agentId ?? null) === (scope.agentId ?? null) &&
  (chat.projectId ?? null) === (scope.projectId ?? null) &&
  (chat.guidedPreset
    ? !!scope.guidedPresets?.includes(chat.guidedPreset)
    : !scope.guidedPresets?.length);

/** Whether ``scope`` shows a draft only an entry point can prepare. */
export const scopeNeedsPreparedDraft = (scope: DraftScope): boolean =>
  !!(scope.agentId || scope.projectId || scope.guidedPresets?.length);

/**
 * The addresses a fresh chat opens under before it exists on the server:
 * ``/chat/new``, and the same inside an agent's or a project's frame
 * (``/agents/{id}/chat/new``, ``/projects/{id}/chat/new``); for a project its
 * start page as well (``PROJECT_HOME_ROUTE``). The session is
 * created by the first message; the address then names it.
 */
const NEW_CHAT_ROUTE = /^(?:\/(agents|projects)\/([^/]+))?\/chat\/new\/?$/;

/**
 * A project's start page (KAN-2107): a new chat of the project too, private,
 * with the project's chats listed under its composer. ``/projects/new`` is the
 * project builder's start, not a project.
 */
const PROJECT_HOME_ROUTE = /^\/projects\/(?!new\/?$)([^/]+)\/?$/;

/**
 * The guided chats embedded in the Skills and Projects areas, as drafts: the
 * ``new`` in place of the chat id, next to ``/skills/new/{chatId}`` and the
 * others they become. Each hosts the presets listed.
 */
const GUIDED_DRAFT_ROUTES: Record<string, readonly string[]> = {
  '/skills/new/new': ['skill-builder', 'connected-skill-builder'],
  '/skills/edit/new': ['skill-editor'],
  '/projects/new/new': ['project-builder'],
};

/** The query parameter that names the guided preset of a ``/chat/new`` draft. */
export const GUIDED_PRESET_PARAM = 'guided';

const trimSlash = (pathname: string) =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

/**
 * The scope a new-chat address belongs to, or ``null`` for any other address.
 * ``guidedPreset`` is the ``guided`` query parameter of ``/chat/new``: the
 * guided chats that run in the plain chat frame (app builder, adding
 * knowledge) open their draft there.
 */
export function parseNewChatRoute(
  pathname: string | null | undefined,
  guidedPreset?: string | null
): DraftScope | null {
  const path = trimSlash(pathname ?? '');
  const guided = GUIDED_DRAFT_ROUTES[path];
  if (guided) return { guidedPresets: guided };
  const home = path.match(PROJECT_HOME_ROUTE);
  if (home) return { projectId: home[1] };
  const match = path.match(NEW_CHAT_ROUTE);
  if (!match) return null;
  if (match[1] === 'agents') return { agentId: match[2] };
  if (match[1] === 'projects') return { projectId: match[2] };
  return guidedPreset ? { guidedPresets: [guidedPreset] } : {};
}

/** Whether a new-chat address renders inside a section frame rather than the chat's own. */
export function isEmbeddedNewChatRoute(pathname: string | null | undefined): boolean {
  const path = trimSlash(pathname ?? '');
  if (GUIDED_DRAFT_ROUTES[path] || PROJECT_HOME_ROUTE.test(path)) return true;
  return !!path.match(NEW_CHAT_ROUTE)?.[1];
}

/**
 * Whether a new-chat address leaves for its chat with a router navigation once
 * the first message created the session, rather than swapping the address in
 * place. The project's start page shows no thread: the chat opens on its own
 * page (``/projects/{id}/chat/{sessionId}``) and the answer streams on there.
 */
export function opensChatByNavigation(pathname: string | null | undefined): boolean {
  return PROJECT_HOME_ROUTE.test(trimSlash(pathname ?? ''));
}

/** Where a new-chat address goes once its first message created the session. */
export function persistedChatPath(
  pathname: string | null | undefined,
  sessionId: string
): string | null {
  const path = trimSlash(pathname ?? '');
  if (GUIDED_DRAFT_ROUTES[path]) return `${path.slice(0, -'new'.length)}${sessionId}`;
  const home = path.match(PROJECT_HOME_ROUTE);
  if (home) return `/projects/${home[1]}/chat/${sessionId}`;
  const match = path.match(NEW_CHAT_ROUTE);
  if (!match) return null;
  return match[1] ? `/${match[1]}/${match[2]}/chat/${sessionId}` : `/chat/${sessionId}`;
}

/** Where each guided preset starts: its draft address, and where that address falls back to. */
const GUIDED_PRESET_HOMES: Record<string, { draft: string; start: string }> = {
  'skill-builder': { draft: '/skills/new/new', start: '/skills' },
  'connected-skill-builder': { draft: '/skills/new/new', start: '/skills' },
  'skill-editor': { draft: '/skills/edit/new', start: '/skills' },
  'project-builder': { draft: '/projects/new/new', start: '/projects' },
  'app-builder': { draft: '/chat/new', start: '/apps' },
  'app-editor': { draft: '/chat/new', start: '/apps' },
  'app-restore': { draft: '/chat/new', start: '/apps' },
  'knowledge-contributor': { draft: '/chat/new', start: '/settings/knowledge' },
};

/** The address a prepared draft of ``preset`` opens under. */
export function guidedDraftPath(preset: string): string {
  const draft = GUIDED_PRESET_HOMES[preset]?.draft ?? '/chat/new';
  return draft === '/chat/new'
    ? `${draft}?${GUIDED_PRESET_PARAM}=${encodeURIComponent(preset)}`
    : draft;
}

/**
 * Where a guided chat's new-chat address without a prepared draft sends the
 * user: the start page its entry point lives on. An agent's or a project's
 * new-chat address needs no fallback, its chat page prepares the draft itself.
 */
export function newChatFallbackPath(scope: DraftScope): string {
  const preset = scope.guidedPresets?.[0];
  if (preset) return GUIDED_PRESET_HOMES[preset]?.start ?? '/chat/new';
  return '/chat/new';
}
